import React, { useState, useEffect, useRef } from 'react';
import cn from 'classnames';
import wrap from 'lodash/wrap';
import Popover from 'bootstrap/js/dist/popover';
import Carousel, {
	ICarouselProps,
	CarouselItem,
} from 'tbk-components/src/components/Carousel';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Image, { IImage } from 'tbk-components/src/components/Image';
import Button, { ILink } from 'tbk-components/src/components/Button';

export interface ILocation {
	/**
	 * Location Image - Image should be 480px X 320px
	 */
	image?: IImage;
	/**
	 * Location Mobile Heading
	 */
	heading?: string;
	/**
	 * Location Stats
	 */
	stats: string[];
	/**
	 * Location Mobile Link
	 */
	link?: ILink;
	/**
	 * Location Left Position (Percentage)
	 */
	leftPos?: number;
	/**
	 * Location Top Position (Percentage)
	 */
	topPos?: number;
}

export interface ILocationMapProps extends IBaseElementProps {
	/**
	 * Location Map Heading
	 */
	heading?: string;
	/**
	 * Location Map Tagline
	 */
	tagline?: string;
	/**
	 * Location Map Image
	 */
	mapImage?: IImage;
	/**
	 * Locations
	 */
	locations?: ILocation[];
}

/**
 * Location Map
 * @block
 * @requiresHooks
 * @icon location
 */
const LocationMap: React.FC<ILocationMapProps> = ({
	className,
	classNames = [],
	heading,
	tagline,
	mapImage,
	locations = [],
}) => {
	const ref = useRef<HTMLDivElement>(null);

	locations.map((location, index) => {
		// if the location link is blank, then set default text
		if (location.link.label == '') {
			location.link.label = 'Donate to a region in ' + location.heading;
		}
	});

	useEffect(() => {
		if (!ref.current) {
			return;
		}

		const initPopovers = () => {
			const popoverTriggerList = [].slice.call(
				ref.current.querySelectorAll('.location-map__map-marker'),
			);

			popoverTriggerList.map(function (popoverTriggerEl) {
				// Pass the element itself as the container rather than relying on the
				// data-bs-container selector, which Bootstrap resolves against the global
				// document and fails inside the block editor's canvas iframe.
				return new Popover(popoverTriggerEl, {
					container: popoverTriggerEl,
				});
			});
		};

		initPopovers();

		const imageEl = ref.current.querySelector(
			'.location-map__map',
		) as HTMLImageElement;

		const initSpots = () => {
			const spots = ref.current.querySelectorAll(
				'.location-map__map-marker',
			) as NodeListOf<HTMLDivElement>;

			if (!spots) {
				return;
			}

			const imgWidth = imageEl.scrollWidth;
			const imgHeight = imageEl.scrollHeight;

			spots.forEach((spot) => {
				const spotEl = spot;
				const { posLeft, posTop } = spotEl.dataset;
				const left = (parseFloat(posLeft) / 100) * imgWidth;
				const top = (parseFloat(posTop) / 100) * imgHeight;

				spotEl.style.left = `${left}px`;
				spotEl.style.top = `${top}px`;
			});
		};

		initSpots();

		window.addEventListener('resize', initSpots);
		imageEl.addEventListener('load', initSpots);

		const initDonorToolSpots = () => {
			const parentEl = ref.current.parentElement;

			if (!parentEl) {
				return;
			}

			if (parentEl.classList.contains('tab-pane')) {
				var tabPaneObserver = new MutationObserver(function (event) {
					const target = event[0].target as HTMLDivElement;
					if (target.classList.contains('active')) {
						initSpots();
					}
				});

				tabPaneObserver.observe(parentEl, {
					attributes: true,
					attributeFilter: ['class'],
					childList: false,
					characterData: false,
				});
			}
		};

		initDonorToolSpots();

		return () => {
			window.removeEventListener('resize', initSpots);
			imageEl.removeEventListener('load', initSpots);
		};
	}, [locations]);

	return (
		<div
			className={className || cn('location-map', ...classNames)}
			ref={ref}
		>
			<div className="location-map__container container-fluid">
				{heading && (
					<h2 className="location-map__heading">{heading}</h2>
				)}
				{tagline && <p className="location-map__tagline">{tagline}</p>}
			</div>
			<div className="location-map__map-container container d-none d-md-block">
				{mapImage && (
					<Image
						className="location-map__map img-fluid"
						{...mapImage}
					/>
				)}
				{locations.length > 0 && (
					<div className="location-map__map-spots">
						{locations.map((location, index) => {
							let locationStats = '';
							if (location.stats.length > 0) {
								locationStats =
									'<span>' +
									location.stats.join('</span><span>') +
									'</span>';
							}
							const locationMapMarker = 'map-marker-' + index;
							const popoverContent = `<div class="map-popover-content"><h3>${location.heading}</h3><div class="popover-stats">${locationStats}</div> <a tabindex="0" class="btn btn-dark-2 location-map__mobile-location-link" href="${location.link.href}" >${location.link.label}</a></div>`;
							return (
								<a
									className={cn(
										'location-map__map-marker ' +
											locationMapMarker,
									)}
									role="button"
									tabIndex={0}
									data-bs-toggle="popover"
									data-bs-container={'.' + locationMapMarker}
									data-bs-offset="40, 10"
									data-bs-html="true"
									data-bs-trigger="focus"
									data-bs-content={popoverContent}
									data-pos-top={location.topPos}
									data-pos-left={location.leftPos}
									key={index}
								>
									{location.image && (
										<Image
											className="marker-image"
											{...location.image}
											alt={location.link.label}
										/>
									)}
								</a>
							);
						})}
					</div>
				)}
			</div>
			<div className="location-map__mobile-locations container d-md-none">
				{locations.length > 0 && (
					<div className="row">
						<Carousel
							id="location-map-carousel"
							className={
								className || cn('location-map__mobile-carousel')
							}
							showBullets={false}
							perView={1}
						>
							{locations.map((location, index) => (
								<CarouselItem key={index}>
									<div key={index} className="col-12">
										<div className="location-map__mobile-location">
											{location.image && (
												<div className="location-map__mobile-location-image">
													<Image
														{...location.image}
													/>
												</div>
											)}
											<div className="location-map__mobile-location-content">
												{location.heading && (
													<h3 className="location-map__mobile-location-heading">
														{location.heading}
													</h3>
												)}
												{location.stats.length > 0 && (
													<div className="location-map__mobile-location-stats">
														{location.stats.map(
															(stat, index) => (
																<span
																	key={index}
																	className="location-map__mobile-location-stat"
																>
																	{stat}
																</span>
															),
														)}
													</div>
												)}
												{location.link && (
													<Button
														className="btn btn-dark-2 location-map__mobile-location-link"
														{...location.link}
													/>
												)}
											</div>
										</div>
									</div>
								</CarouselItem>
							))}
						</Carousel>
					</div>
				)}
			</div>
		</div>
	);
};

export default LocationMap;
